import React from "react";
import {
  Box,
  FormControl,
  Select,
  MenuItem,
  makeStyles,
  Typography,
  InputLabel,
  Grid,
  MenuProps,
  useTheme,
  SvgIcon,
  FormHelperText,
} from "@material-ui/core";
import HelpIcon, { HelpProps } from "./HelpIcon";
import { Theme } from "../.themes";
import clsx from "clsx";
import { BrandsEnum } from "../enums";

const useStyles = makeStyles((theme: Theme) => ({
  header: {
    textAlign: "left",
  },
  box: {
    display: "flex",
    alignItems: "center",
    "& > label": {
      position: "relative",
    },
  },
  selection: {
    marginTop: ".5rem",
  },
  selectionPolestar: {
    backgroundColor: "#ECECE7",
  },
  help: {
    textAlign: "right",
  },
  helpPolestar: {
    "& .MuiIconButton-root": {
      backgroundColor: "black",
      color: "white",
    },
    "& .MuiIconButton-sizeSmall": {
      padding: "0px",
    },
    textAlign: "right",
  },
  menuItem: {
    height: 48,
  },
  errorMessage: {
    color: theme.palette.error.main,
  },
}));

const menuProps: Partial<MenuProps> = {
  anchorOrigin: {
    horizontal: "left",
    vertical: "top",
  },
  transformOrigin: {
    horizontal: "left",
    vertical: "top",
  },
  getContentAnchorEl: null,
};

interface ListValue {
  value: any;
  title: string;
}

interface DropdownSelectorBaseProps {
  title?: string;
  help?: HelpProps;
  label?: { id?: string; title?: string };
  selected?: any;
  values?: ListValue[];
  onChange: Function;
  dropdownId?: string;
  showError?: boolean;
  errorMessage?: string;
}

const DropdownSelectorBase: React.FC<DropdownSelectorBaseProps> = (props: DropdownSelectorBaseProps) => {
  const theme: Theme = useTheme();
  const classes = useStyles(theme);
  const label = props.label;
  let labelElement;
  if (label && label?.title) {
    labelElement = (
      <InputLabel id={label?.id} shrink={false}>
        {label.title}
      </InputLabel>
    );
  }
  const isPolestar = theme.brand === BrandsEnum.Polestar || theme.brand === BrandsEnum.PolestarForetag;

  const help = props.help;
  const helpElement = help && (help.title || help.description) ? <HelpIcon title={help.title} description={help.description} /> : null;

  let labelArea;
  if (labelElement) {
    labelArea = (
      <Grid container direction='row' alignItems='center'>
        <Grid item xs={11}>
          {labelElement}
        </Grid>
        <Grid item xs={1} className={isPolestar ? clsx(classes.help, classes.helpPolestar) : classes.help}>
          {helpElement}
        </Grid>
      </Grid>
    );
  }

  function handleOnChange(newValue: any, onChange: Function) {
    if (onChange) {
      onChange(newValue);
    }
  }

  const icon: React.ElementType | undefined =
    theme.icons?.selectIcon && ((p => <SvgIcon {...p}>{theme.icons?.selectIcon}</SvgIcon>) as React.ElementType);

  return (
    <Box id={"ws-dropdown-" + props.label?.id}>
      {props.title && <Typography variant='h2'>{props.title}</Typography>}
      {labelArea}
      <FormControl fullWidth>
        <Select
          IconComponent={icon}
          MenuProps={menuProps}
          className={isPolestar ? clsx(classes.selection, classes.selectionPolestar) : classes.selection}
          labelId={props?.label?.id}
          id={props?.dropdownId}
          value={props.selected}
          onChange={e => handleOnChange(e.target.value, props.onChange)}
          variant='outlined'
          error={props.showError}
        >
          {props.values?.map(i => (
            <MenuItem className={classes.menuItem} key={i.value} value={i.value}>
              {i.title}
            </MenuItem>
          ))}
        </Select>
        {props.showError && <FormHelperText className={classes.errorMessage}>{props.errorMessage}</FormHelperText>}
      </FormControl>
    </Box>
  );
};
export default DropdownSelectorBase;
